/**
 * Turn the served lens manifests into what the switcher and sidebars draw.
 *
 * Pure and synchronous: fetching lives in useLenses.js, the user's access in
 * components/lens/lensAccess.js. Given the GET /lenses body and an access
 * snapshot, every lens comes back with only the pages and sections that user's
 * sidebar shows, its landing path, and whether it has anything to show at all.
 *
 * Resolved lens:
 *   { id, kind: 'use-case' | 'role', label, blurb, abbr, color, order,
 *     composes: [labels of the use cases a role bundles],
 *     defaultForRoles: [normalized role names],
 *     groups: [{ key, label | labelKey, icon, items: [{ id, to, labelKey, icon }] }],
 *     sections: [classic-sidebar section keys, in order],
 *     home: path | null, visible: boolean }
 */
import { NAV_PAGES } from '../nav/catalog';
import {
  canSeeSection, normalizeRoleName, owns, ownsSection, passes, productCount, sectionHomePath,
} from '../../components/lens/lensAccess';

const START_GROUP = { key: 'start', labelKey: 'lens.groups.start', icon: 'compass' };

/** A catalog page, if its own gate passes and the sidebar section it sits in would show. */
const visiblePage = (id, access) => {
  const p = NAV_PAGES[id];
  if (!p || !passes(p.requires, access)) return null;
  if (p.section && !canSeeSection(p.section, access)) return null;
  return { id, to: p.to, labelKey: p.labelKey, icon: p.icon };
};

const pagesOf = (ids, access, taken) => ids
  .filter((id) => !taken.has(id))
  .map((id) => visiblePage(id, access))
  .filter(Boolean)
  .map((page) => { taken.add(page.id); return page; });

/** A use case's own groups, keyed by `prefix` so two use cases in one role never collide. */
const groupsOf = (lens, access, taken, prefix = '') => (lens.groups || [])
  .map((g) => ({
    key: `${prefix}${g.key}`, label: g.label, icon: g.icon, items: pagesOf(g.items, access, taken),
  }))
  .filter((g) => g.items.length > 0);

const sectionsOf = (keys, access) => (keys || []).filter((k) => canSeeSection(k, access));

/**
 * A catalog page the org has BOUGHT (not just one the sidebar shows: an
 * unlicensed page shows too, locked). Decides whether a lens is worth offering.
 */
const ownedPage = (id, access) => {
  const p = NAV_PAGES[id];
  return Boolean(p) && owns(p.requires, access) && (!p.section || ownsSection(p.section, access));
};

const refVisible = (ref, access) => (ref?.page
  ? ownedPage(ref.page, access)
  : Boolean(ref?.section) && ownsSection(ref.section, access));

/**
 * Is the use case worth offering this user? Only when one of its `requires_any`
 * (the pages or modules it is about) shows: AI SOC needs Signals or Incidents,
 * not just the Atlas pages it borrows. A manifest without the field falls back
 * to "anything of its own shows".
 */
const useCaseVisible = (lens, access) => {
  if (Array.isArray(lens.requires_any) && lens.requires_any.length > 0) {
    return lens.requires_any.some((ref) => refVisible(ref, access));
  }
  return (lens.groups || []).some((g) => g.items.some((id) => ownedPage(id, access)))
    || (lens.sections || []).some((k) => ownsSection(k, access));
};

const homeOf = (home, groups, sections, access) => {
  if (home?.page) {
    const page = visiblePage(home.page, access);
    if (page) return page.to;
  }
  if (home?.section && canSeeSection(home.section, access)) {
    const path = sectionHomePath(home.section, access);
    if (path) return path;
  }
  const first = groups.find((g) => g.key !== START_GROUP.key)?.items[0];
  if (first) return first.to;
  for (const key of sections) {
    const path = sectionHomePath(key, access);
    if (path) return path;
  }
  return groups[0]?.items[0]?.to || null;
};

const assemble = (base, ownGroups, ownSections, frame, access, taken) => {
  const start = { ...START_GROUP, items: pagesOf(frame.top || [], access, taken) };
  const groups = [...(start.items.length ? [start] : []), ...ownGroups];
  const sections = [...new Set([...ownSections, ...sectionsOf(frame.bottom, access)])];
  return { ...base, groups, sections, home: homeOf(base.manifestHome, groups, ownSections, access) };
};

const baseOf = (m) => ({
  id: m.id,
  kind: m.kind,
  label: m.label,
  blurb: m.blurb,
  abbr: m.abbr,
  color: m.color,
  order: m.order,
  manifestHome: m.home,
  composes: [],
  defaultForRoles: (m.default_for_roles || []).map(normalizeRoleName),
});

/**
 * { useCases, roles, byId } for a /lenses response body and a user's access.
 * An empty or malformed body resolves to no lenses (the switcher then hides),
 * and so does an org with fewer products than the frame's `min_products`.
 */
const NONE = Object.freeze({ useCases: [], roles: [], byId: new Map() });

export const resolveLenses = (body, access) => {
  const frame = body?.frame || { top: [], bottom: [] };
  // An org that bought one product (Gemini only, Anthropic only) keeps that
  // product's own sidebar: no lens is offered or picked for its role.
  if (productCount(access) < (frame.min_products ?? 2)) return NONE;
  const rawUseCases = Array.isArray(body?.use_cases) ? body.use_cases : [];
  const rawRoles = Array.isArray(body?.roles) ? body.roles : [];
  const ucById = new Map(rawUseCases.map((u) => [u.id, u]));

  const useCases = rawUseCases.map((u) => {
    const taken = new Set(frame.top || []);
    const resolved = assemble(
      baseOf(u),
      groupsOf(u, access, taken),
      sectionsOf(u.sections, access),
      frame, access, new Set(),
    );
    return { ...resolved, visible: useCaseVisible(u, access) };
  });

  const roles = rawRoles.map((r) => {
    const composed = (r.compose || []).map((id) => ucById.get(id)).filter(Boolean);
    const taken = new Set(frame.top || []);
    // Each use case keeps its own groups, in compose order; a page shared by
    // two of them shows once, under the first.
    const ownGroups = composed.flatMap((u) => groupsOf(u, access, taken, `${u.id}-`));
    const ownSections = [...new Set(composed.flatMap((u) => sectionsOf(u.sections, access)))];
    const resolved = assemble(baseOf(r), ownGroups, ownSections, frame, access, new Set());
    return {
      ...resolved,
      composes: composed.map((u) => u.label),
      visible: composed.some((u) => useCaseVisible(u, access)),
    };
  });

  const byId = new Map([...useCases, ...roles].map((l) => [l.id, l]));
  return { useCases, roles, byId };
};

/** The first visible role lens whose defaults include one of the user's roles, or null. */
export const roleDefaultLens = (resolved, access) => resolved.roles
  .find((r) => r.visible && r.defaultForRoles.some((name) => access.roles.includes(name))) || null;
